/** Club cannac — atmospheric frame for Learn. Fiction only; curriculum stays factual. */

export const CLUB = {
  name: "Club cannac",
  tagline: "A members’ club that doesn’t exist yet",
  promise: "Literacy is the only cultivation we do here.",
  lintel: "Members & the curious — Class B stays outside",
} as const;

export type ClubRoomId =
  | "paths"
  | "explore"
  | "access"
  | "lessons"
  | "timeline"
  | "faqs"
  | "terms";

export type ClubRoom = {
  id: ClubRoomId;
  name: string;
  short: string;
  host: string;
  door: string;
};

/** Mode id → room scenography */
export const CLUB_ROOMS: ClubRoom[] = [
  {
    id: "paths",
    name: "Cloakroom",
    short: "Tours",
    host: "Leave your preconceptions with the attendant. Pick a tour tag — or the Founders’ Tour if you want the whole house.",
    door: "Cloakroom",
  },
  {
    id: "explore",
    name: "Tasting Bar",
    short: "Bar",
    host: "No pours that break the law — only chemotypes, overheard myths, and a chalkboard of UK numbers.",
    door: "Tasting Bar",
  },
  {
    id: "access",
    name: "Clinic Annex",
    short: "Access",
    host: "Literacy directory of UK clinics & specialty pharmacies — not a referral desk. The market moves; check the regulator before you book.",
    door: "Clinic Annex",
  },
  {
    id: "lessons",
    name: "Library",
    short: "Library",
    host: "Pull a spine. Read Plain, Clear, or Deep. Stamp the folio when you’re done — membership fills with resin.",
    door: "Library",
  },
  {
    id: "timeline",
    name: "Members’ Wall",
    short: "Wall",
    host: "Framed years of UK cannabis policy. Walk the corridor. Notice who got ignored.",
    door: "Members’ Wall",
  },
  {
    id: "faqs",
    name: "Concierge",
    short: "Desk",
    host: "Ask anything. We’ll stamp ANSWERED — without inventing miracles or grow tips.",
    door: "Concierge",
  },
  {
    id: "terms",
    name: "Cellar Catalogue",
    short: "Cellar",
    host: "A–Z bins. Plain / Clear / Deep tasting notes for every term in the house.",
    door: "Cellar",
  },
];

export const HOUSE_RULES = [
  "Recreational cannabis remains Class B in the UK. This club is literacy, not a licence.",
  "No driving after the tasting bar — even when the pour is only a metaphor.",
  "Literacy is the only cultivation we do here.",
  "Miracle cures are refused at the door. Evidence grades are welcome.",
  "Indica / sativa / skunk are folk nicknames. Chemotype is the map.",
] as const;

/** Chapter colour stains for spines, tags, and stage tint */
export const CHAPTER_STAINS: Record<
  string,
  { stain: string; soft: string; label: string }
> = {
  plant: { stain: "#3ecf7a", soft: "rgba(62, 207, 122, 0.18)", label: "Plant" },
  chemistry: { stain: "#e8b84a", soft: "rgba(232, 184, 74, 0.18)", label: "Chemistry" },
  methods: { stain: "#d4896a", soft: "rgba(212, 137, 106, 0.18)", label: "Methods" },
  medical: { stain: "#6ba3c9", soft: "rgba(107, 163, 201, 0.18)", label: "Medical" },
  "uk-law": { stain: "#c45c6a", soft: "rgba(196, 92, 106, 0.2)", label: "UK law" },
  culture: { stain: "#a8896c", soft: "rgba(168, 137, 108, 0.2)", label: "Culture" },
};

export function roomById(id: ClubRoomId): ClubRoom {
  return CLUB_ROOMS.find((r) => r.id === id) ?? CLUB_ROOMS[0];
}

export function pathDisplayTitle(pathId: string, title: string): string {
  if (pathId === "cannac-complete") return "Founders’ Tour";
  return title;
}

export function pathTicketLabel(pathId: string): string {
  if (pathId === "cannac-complete") return "FOUNDERS";
  if (pathId === "start-here") return "GUEST";
  if (pathId === "safer-use") return "SAFER";
  if (pathId === "uk-law-crash") return "LAW";
  if (pathId === "medical-context") return "CLINIC";
  if (pathId === "chemistry-literacy") return "CHEM";
  if (pathId === "culture-history") return "CULTURE";
  return "TOUR";
}
